import * as React from "react";
import {OnFailure, OnSuccess, OnSuccessData} from "../../../../../store/action/actionHelpers";
import {connect} from "react-redux";
import {Dispatch} from "redux";
import Toast from "../../../../helper/Toast";
import {none, option, Option} from "ts-option";
import Address from "../../../../../model/address/Address";
import PartnerBrand from "../../../../../model/partnerBrand/PartnerBrand";
import {
	addPartnerBrandRewardsPoints,
	createPartnerBrand,
	fetchQuoteEmailStyles,
	updateRewardsData
} from "../../../../../store/action/partnerBrand/partnerBrand";
import CreatePartnerBrandFormView from "./CreatePartnerBrandFormView";
import PartnerBrandPoints from "../../../../../model/partnerBrand/PartnerBrandPoints";
import {getLoggedUser} from "../../../../../store/view/user";
import PartnerBrandRewardsData from "../../../../../model/partnerBrand/PartnerBrandRewardsData";
import PartnerBrandBankData from "../../../../../model/partnerBrand/PartnerBrandBankData";
import {notifyBugsnagAndShowToast} from "../../../../../utils/Utils";
import QuoteEmailStyle from "../../../../../model/partnerBrand/QuoteEmailStyle";
import {RootState} from "../../../../../store/reducer/root/RootState";
import Loading from "../../../../ui/loading/Loading";
import {fetchMerchants} from "../../../../../store/action/merchant/merchant";
import DictionaryItem from "../../../../../model/dictionary/DictionaryItem";
import CustomField from "../../../../../model/customField/CustomField";
import CustomFieldValue from "../../../../../model/job/CustomFieldValue";
import {api} from "../../../../../api/api";
import {fetchPartnerBrandCustomFields} from "../../../../../store/action/customField/customField";
import JobType from "../../../../../model/job/JobType";
import {fetchJobTypes} from "../../../../../store/action/jobType/jobType";

interface IComponentProps {
	/**
	 * On successful created brand callback.
	 */
	onSuccess: () => void;
	/**
	 * On cancel button click.
	 */
	onCancel: () => void;
}

interface IReduxProps {
	/**
	 * Quote email templates styles
	 */
	quoteEmailStyles: Option<QuoteEmailStyle[]>;
	/**
	 * Suffix of default quote email style
	 */
	defaultQuoteEmailStyleSuffix: Option<string>;
	/**
	 * All asset categories.
	 */
	merchants: Option<DictionaryItem[]>;
	/**
	 * Partner brand custom fields list.
	 */
	partnerBrandCustomFields: Option<CustomField[]>;
	/**
	 * All globally available job types.
	 */
	jobTypes: JobType[];
}

interface IDispatchProps {
	/**
	 * Fetch quote email templates styles
	 * @param {OnSuccess} onSuccess
	 * @param {OnFailure} onFailure
	 */
	fetchQuoteEmailStyles: (onSuccess: OnSuccess, onFailure: OnFailure) => void
	/**
	 * Create partner brand.
	 * @param {partnerBrand} Partner brand
	 * @param {Option<File>} Partner brand logo
	 * @param {Option<Address>} address
	 * @param {OnSuccess} onSuccess
	 * @param {OnFailure} onFailure
	 */
	createPartnerBrand: (partnerBrand: PartnerBrand,
		file: Option<File>,
		address: Option<Address>,
		onSuccess: OnSuccessData<PartnerBrand>,
		onFailure: OnFailure) => void;
	/**
	 * Fetch merchants
	 * @param {OnFailure} onFailure
	 */
	fetchMerchants: (onFailure: OnFailure) => void
	/**
	 * Fetch partner brand custom fields method
	 */
	fetchPartnerBrandCustomFields: () => void;
	/**
	 * Fetch globally available job types.
	 */
	fetchJobTypes: () => void;
}

interface IState {
	/**
	 * Indicate if any server action is in progress.
	 */
	isSubmitting: boolean;
	/**
	 * Brand logo file.
	 */
	file: Option<File>
	/**
	 * Newly added points
	 */
	points: PartnerBrandPoints[]
	/**
	 * True while quoteEmailStyles are fetching
	 */
	isLoading: boolean
	/**
	 * Partner brand custom fields values
	 */
	partnerBrandCustomFieldsValues: Option<CustomFieldValue[]>
}

type Props = IComponentProps & IReduxProps & IDispatchProps;

class CreatePartnerBrandForm extends React.PureComponent<Props, IState> {
	/**
	 * Indicates if rewards related data is available for currently logged user.
	 */
	rewardsDataAvailable: boolean;
	/**
	 * Indicates if bank data is available for currently logged user.
	 */
	bankDataAvailable: boolean;

	constructor(props: Props) {
		super(props);
		this.state = {isSubmitting: false, file: none, points: [], isLoading: true, partnerBrandCustomFieldsValues: none};
		const loggedUser = getLoggedUser();
		this.rewardsDataAvailable = loggedUser.map(u => u.hasRewardsManagementPerm()).getOrElse(false);
		this.bankDataAvailable = loggedUser.map(u => u.isAdmin()).getOrElse(false);
		this.onSubmit = this.onSubmit.bind(this);
		this.onAddFile = this.onAddFile.bind(this);
		this.onAddPoints = this.onAddPoints.bind(this);
		this.updateCustomFieldsValues = this.updateCustomFieldsValues.bind(this);
	}

	componentDidMount() {
		if (this.props.quoteEmailStyles.isEmpty) {
			this.props.fetchQuoteEmailStyles(
				() => {this.setState({isLoading: false})},
				(err) => notifyBugsnagAndShowToast(err, "Fetch quote email templates styles"));
		}
		else {
			this.setState({isLoading: false});
		}
		if (this.props.merchants.isEmpty) {
			this.props.fetchMerchants((e) => notifyBugsnagAndShowToast(e, "Fetch merchants"))
		}

		if (!this.props.partnerBrandCustomFields.isDefined) {
			this.props.fetchPartnerBrandCustomFields();
		}

		if (!this.props.jobTypes.length) {
			this.props.fetchJobTypes();
		}
	}

	render() {
		return (
			<Loading isLoading={this.state.isLoading || !this.props.jobTypes.length}>
				<CreatePartnerBrandFormView onSubmit={this.onSubmit}
											onCancel={this.props.onCancel}
											isSubmitting={this.state.isSubmitting}
											onAddFile={this.onAddFile}
											onRemoveFile={() => this.setState({file: none})}
											onAddPoints={this.onAddPoints}
											points={this.state.points.slice().reverse()}
											rewardFieldsAvailable={this.rewardsDataAvailable}
											defaultQuoteEmailStyleSuffix={this.props.defaultQuoteEmailStyleSuffix}
											bankFieldsAvailable={this.bankDataAvailable}
											customFields={this.props.partnerBrandCustomFields}
											customFieldsValues={this.state.partnerBrandCustomFieldsValues}
											updateCustomFieldsValues={this.updateCustomFieldsValues}
											jobTypes={this.props.jobTypes}/>
			</Loading>
		);
	}

	onAddFile(file: File) {
		this.setState({file: option(file)})
	}

	onSubmit(data: PartnerBrand,
		address: Option<Address>,
		partnerBrandRewardsData: Option<PartnerBrandRewardsData>,
		partnerBrandBankData: Option<PartnerBrandBankData>,
		jobTypes: JobType[]) {
		let pbDataToUpdate: PartnerBrand = data;
		if (this.bankDataAvailable) {
			partnerBrandBankData.map(bankData => {
				if (bankData.isProvided()) {
					pbDataToUpdate = pbDataToUpdate.copy({
						bankAccountNumber: option(bankData.bankAccountNumber),
						bankSortCode: option(bankData.bankSortCode),
						accountHolder: bankData.accountHolder
					})
				}
			});
		}
		this.setState({isSubmitting: true}, () => {
			this.props.createPartnerBrand(
				pbDataToUpdate,
				this.state.file,
				address,
				(partnerBrand) => {
					Toast.success("Partner brand created", "Partner brand create");
					this.updatePartnerBrandJobTypesConfig(partnerBrand.id, jobTypes);
					if (this.rewardsDataAvailable) {
						if (this.state.points.length) {
							addPartnerBrandRewardsPoints(this.state.points, partnerBrand.id);
						}
						partnerBrandRewardsData.map(rewardsData => updateRewardsData(partnerBrand.id, rewardsData));
					}
					this.updatePartnerBrandCustomFieldsValues(partnerBrand.id,
						this.state.partnerBrandCustomFieldsValues.getOrElse([]).filter(cfv => cfv.value.isDefined));
					this.props.onSuccess();
				},
				(error: any) => {
					let err: string;
					if (error && error.response && error.response.status == 409)
						err = `Partner brand with name '${pbDataToUpdate.name}' exists`;
					else
						err = "Partner brand create failed";
					this.setState({isSubmitting: false}, () => Toast.error(err, "Partner brand create"));
				}
			);
		});
	}

	onAddPoints(point: PartnerBrandPoints) {
		this.setState({points: this.state.points.concat(point)})
	}

	updateCustomFieldsValues(customFieldsValues: CustomFieldValue[]) {
		this.setState({partnerBrandCustomFieldsValues: option(customFieldsValues)});
	}

	updatePartnerBrandJobTypesConfig(partnerBrandId: string, jobTypes: JobType[]) {
		api.instance.partnerBrandApi.updateJobTypesConfig(partnerBrandId, jobTypes.map(jt => jt.toPbUpdateJson()))
			.catch(err => notifyBugsnagAndShowToast(err, "Set partner brand job types configuration"));
	}

	updatePartnerBrandCustomFieldsValues(partnerBrandId: string, updatedCustomFieldsValues: CustomFieldValue[]) {
		api.instance.partnerBrandApi.updatePartnerBrandCustomFieldsValues(partnerBrandId, updatedCustomFieldsValues)
			.then(
				() => Toast.success('Partner brand custom fields values created', 'Partner brand custom fields values creation'))
			.catch(err => notifyBugsnagAndShowToast(err, "Create partner brand custom fields values"));
	}
}

function mapStateToProps(state: RootState) {
	const quoteEmailStyles = state.partnerBrand.quoteEmailStyles;
	const defaultQuoteEmailStyle = option(quoteEmailStyles.getOrElse([]).filter(qes => qes.defaultStyle)[0]);
	const filteredCustomFields = state.customFields.partnerBrandCustomFields.getOrElse([]).filter(item => !item.disabledInUi);
	return {
		quoteEmailStyles,
		merchants: state.merchant.merchants,
		defaultQuoteEmailStyleSuffix: defaultQuoteEmailStyle.map(dqes => dqes.suffix),
		partnerBrandCustomFields: state.customFields.partnerBrandCustomFields.isDefined ? option(filteredCustomFields) : none,
		jobTypes: state.jobType.jobTypes.getOrElse([])
	}
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
	return {
		fetchQuoteEmailStyles: (onSuccess: OnSuccess, onFailure: OnFailure) =>
			dispatch(fetchQuoteEmailStyles(onSuccess, onFailure)),
		createPartnerBrand: (partnerBrand: PartnerBrand,
			file: Option<File>,
			address: Option<Address>,
			onSuccess: OnSuccessData<PartnerBrand>,
			onFailure: OnFailure) =>
			dispatch(createPartnerBrand(partnerBrand, file, address, onSuccess, onFailure)),
		fetchMerchants: (onFailure: OnFailure) => dispatch(fetchMerchants(() => {}, onFailure)),
		fetchPartnerBrandCustomFields: () => dispatch(
			fetchPartnerBrandCustomFields(() => {},
				(error) => notifyBugsnagAndShowToast(error, "Fetch partner brand custom fields"))
		),
		fetchJobTypes: () => dispatch(
			fetchJobTypes(() => {}, (error) => notifyBugsnagAndShowToast(error, "Fetch job types"))
		)
	};
}

export default connect<IReduxProps, IDispatchProps, IComponentProps>(mapStateToProps, mapDispatchToProps)(CreatePartnerBrandForm);